import {
  isValidGeoIp,
  normalizeCountryCode,
  resolveCountryCodeFromCurrentIp,
  resolveCountryCodeFromIp,
} from '@/utils/ip-country-lookup';

/**
 * @typedef {'profile' | 'loginIp' | 'currentIp' | 'none'} DepositGeoSource
 */

/**
 * @typedef {{ countryCode: string | null, source: DepositGeoSource }} DepositGeoResolution
 */

/**
 * @param {DepositGeoResolution} resolved
 */
function logDepositGeoResolution(resolved) {
  if (process.env.NODE_ENV !== 'development') return;
  console.debug('[deposit-geo]', resolved);
}

/**
 * @param {string | null | undefined} value
 * @returns {string | null}
 */
function readProfileCountryCode(value) {
  return normalizeCountryCode(value);
}

/**
 * 充值 geo 统一国家码解析：profile.countryCode → loginIp ∥ 当前出口 IP（优先 loginIp）。
 * @param {object | null | undefined} profile
 * @returns {Promise<DepositGeoResolution>}
 */
export async function resolveDepositCountryCodeFromProfile(profile) {
  const profileCountry = readProfileCountryCode(profile?.countryCode ?? profile?.loginCountryCode);
  if (profileCountry) {
    const resolved = { countryCode: profileCountry, source: 'profile' };
    logDepositGeoResolution(resolved);
    return resolved;
  }

  const loginIp = String(profile?.loginIp ?? '').trim();
  const loginIpPromise =
    loginIp && isValidGeoIp(loginIp) ? resolveCountryCodeFromIp(loginIp) : Promise.resolve(null);

  const [fromLoginIp, fromCurrentIp] = await Promise.all([
    loginIpPromise,
    resolveCountryCodeFromCurrentIp(),
  ]);

  if (fromLoginIp) {
    const resolved = { countryCode: fromLoginIp, source: 'loginIp' };
    logDepositGeoResolution(resolved);
    return resolved;
  }

  if (fromCurrentIp) {
    const resolved = { countryCode: fromCurrentIp, source: 'currentIp' };
    logDepositGeoResolution(resolved);
    return resolved;
  }

  const resolved = { countryCode: null, source: 'none' };
  logDepositGeoResolution(resolved);
  return resolved;
}

/**
 * @param {object | null | undefined} profile
 * @returns {Promise<string | null>}
 */
export async function resolveDepositCountryCode(profile) {
  const resolved = await resolveDepositCountryCodeFromProfile(profile);
  return resolved.countryCode;
}
